// src/services/operatorTwoFactorSignal.ts — what the SERVER last said about the
// signed-in operator's own authenticator, kept beside the profile (ENG-1378).
//
// The profile AuthContext loads at sign-in can be stale in both directions: it
// says "enrolled" after the factor was reset elsewhere, and "not enrolled" for a
// minute after the operator enrols in a dialog. The server's answer to an actual
// request is never stale. So when a request is refused with "set up your
// authenticator first", or enrolment succeeds, that fact is noted here and wins
// over the profile until a fresh profile read agrees with it.
//
// One signal for the whole tab, not one per dialog: whether the operator holds an
// authenticator is a fact about the operator. It is also why ~27 code-field call
// sites need no change of their own — the gate they already render reads it.
import { useSyncExternalStore } from "react";

export type OperatorTwoFactorSignal = "setup-required" | "enrolled" | null;

let signal: OperatorTwoFactorSignal = null;
const listeners = new Set<() => void>();

const set = (next: OperatorTwoFactorSignal) => {
  if (signal === next) return;
  signal = next;
  listeners.forEach((listener) => listener());
};

/** The server refused a request because the operator has no authenticator. */
export const noteOperatorSetupRequired = (): void => set("setup-required");

/** The operator just enrolled (the verify call succeeded). */
export const noteOperatorEnrolled = (): void => set("enrolled");

/** The operator just disabled their factor; the profile rules again. */
export const clearOperatorTwoFactorSignal = (): void => set(null);

/**
 * A fresh profile read landed. Drop the signal only when the profile now agrees
 * with it; a profile that still disagrees is the stale one, and the server's
 * latest word keeps winning.
 */
export const reconcileOperatorTwoFactorSignal = (
  enabled: boolean | undefined
): void => {
  if (signal === "enrolled" && enabled === true) set(null);
  if (signal === "setup-required" && enabled === false) set(null);
};

export const readOperatorTwoFactorSignal = (): OperatorTwoFactorSignal =>
  signal;

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Subscribe a component to the signal. */
export const useOperatorTwoFactorSignal = (): OperatorTwoFactorSignal =>
  useSyncExternalStore(
    subscribe,
    readOperatorTwoFactorSignal,
    readOperatorTwoFactorSignal
  );
